/**
 * ELVOAQ AG — Footer content pages (robust, expandable content)
 * Contact · Press Kit · Docs · API Docs · Blog · Help Center · Status
 * Integrations · Privacy Policy · Terms of Service
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCheckCircle,
  faChevronDown,
  faCode,
  faDatabase,
  faEnvelope,
  faFileAlt,
  faHeadset,
  faMapMarkerAlt,
  faNewspaper,
  faPhone,
  faPuzzlePiece,
  faRocket,
  faShieldAlt,
  faStar,
  faTimes,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faGithub, faSlack, faTwitter } from '@fortawesome/free-brands-svg-icons';
import '../styles/Homepage.css';

/* ── Shared back-button nav (scrolls to top on mount) ── */
const PageNav = ({ title }: { title: string }) => {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <nav className="hp-info-nav">
      <button className="hp-info-back" onClick={() => navigate('/home')}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
      </button>
      <span className="hp-info-nav__title">{title}</span>
    </nav>
  );
};

/* ════════════════════════════════════════════════
   CONTACT
   ════════════════════════════════════════════════ */
export const ContactPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Contact Us" />
    <div className="hp-info-page__hero">
      <h1>Get in <span className="gradient-text">Touch</span></h1>
      <p>We'd love to hear from you. Reach out to our team in Zürich.</p>
    </div>
    <div className="hp-info-page__content">
      <div className="hp-info-grid hp-info-grid--2">
        <div className="hp-info-card hp-info-card--contact">
          <h3><FontAwesomeIcon icon={faEnvelope} /> Email Us</h3>
          <form className="hp-info-form" onSubmit={e => { e.preventDefault(); alert('Message sent! We\'ll respond within 24 hours.'); }}>
            <div className="hp-info-form__row">
              <input type="text" placeholder="Your Name" required />
              <input type="email" placeholder="Your Email" required />
            </div>
            <select>
              <option>General Inquiry</option>
              <option>Sales / Enterprise</option>
              <option>Technical Support</option>
              <option>Partnership</option>
              <option>Press / Media</option>
            </select>
            <textarea placeholder="Your Message" rows={6} required />
            <button type="submit" className="hp-btn hp-btn--primary">
              Send Message <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </form>
        </div>
        <div className="hp-info-card hp-info-card--contact-info">
          <h3><FontAwesomeIcon icon={faMapMarkerAlt} /> Our Office</h3>
          <div className="hp-contact-detail">
            <strong>ELVOAQ AG</strong>
            <p>Bahnhofstrasse 21<br />8001 Zürich<br />Switzerland</p>
          </div>
          <div className="hp-contact-detail">
            <strong><FontAwesomeIcon icon={faPhone} /> Phone</strong>
            <p>+41 44 000 00 00</p>
          </div>
          <div className="hp-contact-detail">
            <strong><FontAwesomeIcon icon={faEnvelope} /> Email</strong>
            <p>hello@elvoaq.ch</p>
          </div>
          <div className="hp-contact-detail">
            <strong><FontAwesomeIcon icon={faHeadset} /> Support</strong>
            <p>support@elvoaq.ch<br />Mon–Fri, 9:00–18:00 CET</p>
          </div>
          <div className="hp-contact-social">
            <a href="https://twitter.com/elvoaq" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><FontAwesomeIcon icon={faTwitter} /></a>
            <a href="https://github.com/elvoaq" target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FontAwesomeIcon icon={faGithub} /></a>
            <a href="https://elvoaq.slack.com" target="_blank" rel="noopener noreferrer" aria-label="Slack"><FontAwesomeIcon icon={faSlack} /></a>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════
   PRESS KIT
   ════════════════════════════════════════════════ */
export const PressPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Press Kit" />
    <div className="hp-info-page__hero">
      <h1>Press <span className="gradient-text">Kit</span></h1>
      <p>Everything you need to write about ELVOAQ AG.</p>
    </div>
    <div className="hp-info-page__content">
      <div className="hp-info-card">
        <h3><FontAwesomeIcon icon={faNewspaper} /> About ELVOAQ AG</h3>
        <p>
          ELVOAQ AG is a Swiss technology company founded in 2020, headquartered in Zürich. We build
          privacy-first, AI-powered software tools for individuals and businesses. Our flagship product,
          ELVOAQ Vault, is an intelligent item management platform trusted by 50,000+ users across 50+ countries.
        </p>
        <div className="hp-info-facts">
          <div className="hp-info-fact"><strong>Founded</strong><span>2020, Zürich</span></div>
          <div className="hp-info-fact"><strong>Headquarters</strong><span>Zürich, Switzerland</span></div>
          <div className="hp-info-fact"><strong>Employees</strong><span>15+</span></div>
          <div className="hp-info-fact"><strong>Users</strong><span>50,000+</span></div>
          <div className="hp-info-fact"><strong>Items Managed</strong><span>2,000,000+</span></div>
          <div className="hp-info-fact"><strong>Countries</strong><span>50+</span></div>
        </div>
      </div>
      <div className="hp-info-card">
        <h3><FontAwesomeIcon icon={faFileAlt} /> Brand Assets</h3>
        <p>Download logos, brand colors, and screenshots for your publication.</p>
        <div className="hp-info-brand-colors">
          <div className="hp-info-swatch" style={{ background: '#3B82F6' }}><span>#3B82F6</span></div>
          <div className="hp-info-swatch" style={{ background: '#22D3EE' }}><span>#22D3EE</span></div>
          <div className="hp-info-swatch" style={{ background: '#06060e' }}><span>#06060E</span></div>
          <div className="hp-info-swatch" style={{ background: '#FFFFFF', color: '#06060e' }}><span>#FFFFFF</span></div>
        </div>
        <p style={{ marginTop: 16 }}>
          <strong>Font:</strong> Inter (Primary), SF Pro (System)
        </p>
      </div>
      <div className="hp-info-card">
        <h3>Media Contact</h3>
        <p>For press inquiries, please contact: <strong>press@elvoaq.com</strong></p>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   DOCUMENTATION  —  robust expandable guide sections
   ════════════════════════════════════════════════════════════════════ */

const DOC_SECTIONS = [
  {
    icon: faRocket, title: 'Getting Started', color: '#3B82F6',
    desc: 'Create your account, add your first items, and learn the basics in under 5 minutes.',
    articles: [
      { title: 'Creating Your Account', content: 'Visit elvoaq.ch and click "Start Free". Enter your email and choose a secure password (minimum 12 characters). You\'ll receive a verification email within seconds — click the link to activate your account. You can also sign up with Google or Apple ID for one-tap onboarding.' },
      { title: 'Adding Your First Item', content: 'From the Dashboard, click the "+" button or press Ctrl+N. Upload a photo (or multiple), and our AI will automatically suggest a category, tags, and estimated value. Review the suggestions, adjust if needed, and hit Save. Your first item is now cataloged with full metadata.' },
      { title: 'Navigating the Dashboard', content: 'The sidebar contains all main sections: Dashboard (overview widgets), Library (all items), Categories (organized groups), Favorites (starred items), and Settings. The top search bar supports natural language — try "blue electronics under CHF 500". Use keyboard shortcuts (press "?" to see the list) for power-user navigation.' },
      { title: 'Importing Existing Data', content: 'Go to Settings → Import/Export. ELVOAQ Vault accepts CSV, JSON, and Excel files. Map your columns to our fields (name, category, value, description, etc.) using the visual mapper. We\'ll validate the data before import and flag any issues. Imports up to 10,000 items are processed in under 30 seconds.' },
      { title: 'Mobile Quick-Add', content: 'On your phone, open ELVOAQ Vault in the browser and use the camera icon for instant photo capture. Our AI processes the image in real-time, categorizing and valuing the item before you finish typing. Perfect for cataloging items on the go — at home, in a store, or during an inventory walk.' },
    ],
  },
  {
    icon: faDatabase, title: 'Item Management', color: '#10B981',
    desc: 'Learn how to add, categorize, tag, and manage items with photos and AI tools.',
    articles: [
      { title: 'Item Fields & Metadata', content: 'Each item supports: Name, Description, Category (auto or manual), Tags (unlimited), Value (purchase price, current value, insured value), Condition (New, Like New, Good, Fair, Poor), Location, Purchase Date, Warranty Expiry, and up to 20 photos. Custom fields can be added per-workspace on Business and Enterprise plans.' },
      { title: 'AI Auto-Categorization', content: 'Upload a photo and our AI analyzes it in 1-3 seconds. It identifies the object, suggests a category from 100+ templates (Electronics, Furniture, Art, Jewelry, etc.), adds relevant tags, and estimates market value. Accuracy is 95%+ for common items. You can correct suggestions to train the model for your specific collection.' },
      { title: 'Bulk Operations', content: 'Select multiple items using checkboxes or Ctrl+Click. Bulk actions include: Move to Category, Add Tags, Update Condition, Change Location, Export Selection, and Delete. The bulk editor also supports field-level edits — change the location of 50 items in one click.' },
      { title: 'Photo Management', content: 'Each item supports up to 20 high-resolution photos. Drag and drop to reorder, click to zoom, and use the full-screen viewer for detailed inspection. Photos are automatically compressed for fast loading while preserving quality. Original files are kept in Swiss cloud storage. Supported formats: JPEG, PNG, WebP, HEIC.' },
      { title: 'Value Tracking & History', content: 'ELVOAQ Vault tracks three value types per item: Purchase Price (what you paid), Current Market Value (AI-estimated), and Insured Value (what your policy covers). The Value History chart shows trends over time. Set alerts to be notified when an item\'s value changes by more than a threshold you define.' },
      { title: 'Duplicate Detection', content: 'When adding a new item, our AI compares it against your existing inventory. If a potential duplicate is found (87%+ match), you\'ll see a side-by-side comparison. Choose to merge (combine metadata), keep both, or discard. Run a full-inventory duplicate scan from Settings → Maintenance.' },
    ],
  },
  {
    icon: faUsers, title: 'Team & Workspaces', color: '#8B5CF6',
    desc: 'Set up team roles, manage permissions, and collaborate across workspaces.',
    articles: [
      { title: 'Creating a Workspace', content: 'Workspaces isolate collections for different purposes — "Home Inventory", "Office Assets", "Art Collection". Go to Settings → Workspaces → Create New. Each workspace has its own items, categories, and team members. Free plans get 1 workspace; Pro gets 3; Business and Enterprise get unlimited.' },
      { title: 'Inviting Team Members', content: 'In Workspace Settings → Members, click "Invite". Enter email addresses (up to 10 at once) and assign a role: Viewer (read-only), Editor (add/edit items), Manager (manage members + settings), or Admin (full control). Invitees receive an email link and can join immediately.' },
      { title: 'Role-Based Access Control', content: 'ELVOAQ Vault uses granular RBAC. Viewer: browse and search items. Editor: add, edit, delete items and upload photos. Manager: everything Editor can do, plus manage members, categories, and workspace settings. Admin: full control including billing, audit logs, and workspace deletion. Custom roles are available on Enterprise.' },
      { title: 'Real-Time Collaboration', content: 'Multiple team members can work simultaneously. See who\'s online with presence indicators. Leave comments on items for discussion. The Activity Feed shows all changes in real-time — who added, edited, or deleted what, and when. @mention team members in comments for notifications.' },
      { title: 'Workspace Transfers & Merging', content: 'Transfer items between workspaces via drag-and-drop or bulk select → Move. Merge two workspaces by going to Settings → Workspaces → Merge. The system handles duplicate detection during merge. All audit history is preserved across transfers.' },
    ],
  },
  {
    icon: faShieldAlt, title: 'Security & Privacy', color: '#EF4444',
    desc: 'Understand our encryption, data handling, 2FA setup, and compliance certifications.',
    articles: [
      { title: 'Encryption Standards', content: 'All data is encrypted at rest using AES-256 and in transit using TLS 1.3. Database fields containing sensitive information use additional application-level encryption. Encryption keys are managed via AWS KMS with automatic rotation every 90 days. We use zero-knowledge architecture — our servers never see your plaintext passwords.' },
      { title: 'Two-Factor Authentication', content: 'Enable 2FA in Settings → Security. We support TOTP apps (Google Authenticator, Authy, 1Password), hardware keys (YubiKey via WebAuthn), and backup codes. 2FA can be enforced workspace-wide by Admins on Business and Enterprise plans. Recovery requires identity verification via our support team.' },
      { title: 'Data Residency & Swiss Hosting', content: 'All data is stored exclusively in Tier IV certified data centers in Zürich and Geneva, Switzerland. We do not replicate data outside of Switzerland. Our infrastructure runs on dedicated servers (not shared cloud) managed by our DevOps team. Swiss data protection law (DSG) provides constitutional-level privacy guarantees.' },
      { title: 'Compliance & Certifications', content: 'ELVOAQ AG holds: ISO 27001 (Information Security Management), SOC 2 Type II (in progress), Swiss DSG compliance, EU GDPR compliance, and CCPA readiness. We undergo annual third-party penetration testing by Swiss security firms. Audit reports are available to Enterprise customers under NDA.' },
      { title: 'Data Export & Deletion', content: 'Export all your data anytime via Settings → Export. Formats: JSON (complete, machine-readable), CSV (spreadsheet-compatible), PDF (printable reports). Full account deletion is available in Settings → Account → Delete Account. All data is permanently removed from our systems within 30 days, including backups.' },
    ],
  },
  {
    icon: faCode, title: 'API Integration', color: '#F59E0B',
    desc: 'Connect ELVOAQ Vault to your existing tools using our REST API.',
    articles: [
      { title: 'Authentication & API Keys', content: 'Generate API keys in Settings → API. Each key can be scoped: read-only, read-write, or admin. Include the key in the Authorization header: "Bearer YOUR_API_KEY". Keys can be rotated without downtime. We recommend separate keys for different integrations for security isolation.' },
      { title: 'Items API', content: 'GET /v2/items — List all items (supports pagination, filtering, sorting). POST /v2/items — Create a new item (JSON body with name, category, value, etc.). PUT /v2/items/:id — Update an item. DELETE /v2/items/:id — Soft-delete an item (moved to trash). GET /v2/items/:id/photos — Get photo URLs. POST /v2/items/:id/photos — Upload photos (multipart/form-data).' },
      { title: 'AI API', content: 'POST /v2/ai/categorize — Send an image (base64 or URL) and receive AI categorization results including suggested category, tags, and estimated value. POST /v2/ai/duplicate-check — Check if an item already exists in your inventory. POST /v2/ai/value-estimate — Get a market value estimate for an item based on description and condition.' },
      { title: 'Webhooks', content: 'Configure webhooks in Settings → API → Webhooks. Events: item.created, item.updated, item.deleted, member.joined, comment.added. Each webhook receives a signed payload (HMAC-SHA256) for verification. Retry logic: 3 attempts with exponential backoff. Test webhooks with our built-in simulator.' },
      { title: 'Rate Limits & Best Practices', content: 'Free: 100 req/hr. Pro: 1,000 req/hr. Business: 10,000 req/hr. Enterprise: custom. Use pagination (limit/offset) for large result sets. Cache responses client-side when possible. Use webhook events instead of polling. Batch operations via POST /v2/items/batch for creating multiple items at once.' },
    ],
  },
  {
    icon: faStar, title: 'Advanced Features', color: '#22D3EE',
    desc: 'Master AI categorization, value prediction, audit trails, and custom dashboards.',
    articles: [
      { title: 'Custom Dashboard Widgets', content: 'Go to Dashboard → Customize. Add widgets by clicking "+". Available widgets: Portfolio Value (total across all items), Category Breakdown (pie chart), Recent Activity (timeline), Value Trends (line chart), Storage Usage, Quick Actions. Drag widgets to reorder. Resize by grabbing corners. Save multiple dashboard layouts.' },
      { title: 'Audit Trail & Compliance', content: 'Every action is logged with immutable timestamps: who did what, when, and from which IP. View the full audit trail in History. Filter by date range, user, action type, or item. Export audit logs as CSV or PDF for compliance reporting. Audit data is retained for 7 years on Business/Enterprise plans.' },
      { title: 'Smart Collections', content: 'Create dynamic collections using rules: "All items where category is Electronics AND value > CHF 1,000" or "Items added in the last 30 days". Collections auto-update as items change. Pin collections to the sidebar for quick access. Share collections with team members or generate public read-only links.' },
      { title: 'Notifications & Alerts', content: 'Configure per-workspace notifications in Settings → Notifications. Alert types: Item value changes (threshold-based), New comments, Team member activity, Warranty expiry reminders, Storage quota warnings. Delivery channels: In-app, Email, Slack integration. Set quiet hours to avoid after-hours notifications.' },
      { title: 'Reports & Export', content: 'Generate reports from the Analytics tab. Report types: Inventory Summary (all items by category), Value Report (total portfolio with breakdown), Insurance Report (formatted for insurance claims), Depreciation Report (value changes over time). Export as PDF, CSV, or Excel. Schedule automated reports on Business plans (weekly/monthly email delivery).' },
    ],
  },
];

const DOC_SECTION_EXTRAS: Record<string, string> = {
  'Getting Started': 'Pro tip: keep your workspace naming consistent and decide on a tagging convention early. This makes search results more accurate and prevents duplicates as your library grows.',
  'Item Management': 'Best practice: review and normalize metadata weekly. Consistent locations, conditions, and value fields will improve analytics accuracy and insurance reporting.',
  'Team & Workspaces': 'For teams, define roles up front and document responsibilities. This reduces accidental changes and keeps audit trails clean for compliance.',
  'Security & Privacy': 'We recommend enabling 2FA and enforcing it at the workspace level. Regularly review access logs and rotate API keys every 90 days.',
  'API Integration': 'When integrating, start with read-only keys and add write permissions only after validation. Use webhooks for event-driven updates to reduce polling.',
  'Advanced Features': 'To get the most value, set quarterly review cycles for reports and dashboards. This helps you spot trends and optimize inventory strategies.'
};

export const DocsPage = () => {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);

  const toggleArticle = (key: string) => setExpandedArticle(expandedArticle === key ? null : key);

  return (
    <div className="hp hp-info-page">
      <PageNav title="Documentation" />
      <div className="hp-info-page__hero">
        <h1><span className="gradient-text">Documentation</span></h1>
        <p>Guides, tutorials, and references to help you get the most from ELVOAQ Vault.</p>
      </div>
      <div className="hp-info-page__content">
        <div className="hp-info-grid hp-info-grid--3">
          {DOC_SECTIONS.map((sec, i) => (
            <div
              className={`hp-info-card hp-info-card--doc ${expandedSection === i ? 'hp-info-card--active' : ''}`}
              key={i}
              onClick={() => setExpandedSection(expandedSection === i ? null : i)}
              style={{ cursor: 'pointer' }}
            >
              <div className="hp-info-card__icon" style={{ background: `${sec.color}15`, color: sec.color }}>
                <FontAwesomeIcon icon={sec.icon} />
              </div>
              <h3>{sec.title}</h3>
              <p>{sec.desc}</p>
              <button
                type="button"
                className="hp-info-card__link"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSection(expandedSection === i ? null : i);
                }}
              >
                {expandedSection === i ? 'Close' : 'Read Guide'}{' '}
                <FontAwesomeIcon icon={expandedSection === i ? faChevronDown : faArrowRight} />
              </button>
            </div>
          ))}
        </div>

        {expandedSection !== null && (
          <div
            className="hp-doc-modal"
            onClick={() => {
              setExpandedSection(null);
              setExpandedArticle(null);
            }}
          >
            <div
              className="hp-doc-modal__panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="hp-doc-modal__header">
                <div className="hp-doc-expanded__icon" style={{ background: `${DOC_SECTIONS[expandedSection].color}15`, color: DOC_SECTIONS[expandedSection].color }}>
                  <FontAwesomeIcon icon={DOC_SECTIONS[expandedSection].icon} />
                </div>
                <div>
                  <h2>{DOC_SECTIONS[expandedSection].title}</h2>
                  <p>{DOC_SECTIONS[expandedSection].desc}</p>
                </div>
                <button
                  type="button"
                  className="hp-doc-modal__close"
                  onClick={() => {
                    setExpandedSection(null);
                    setExpandedArticle(null);
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="hp-doc-articles">
                {DOC_SECTIONS[expandedSection].articles.map((art, j) => {
                  const key = `${expandedSection}-${j}`;
                  const isOpen = expandedArticle === key;
                  const sectionExtra = DOC_SECTION_EXTRAS[DOC_SECTIONS[expandedSection].title] || '';
                  const fullContent = sectionExtra ? `${art.content} ${sectionExtra}` : art.content;
                  return (
                    <div className={`hp-doc-article ${isOpen ? 'hp-doc-article--open' : ''}`} key={j}>
                      <button className="hp-doc-article__toggle" onClick={() => toggleArticle(key)}>
                        <span className="hp-doc-article__num">{String(j + 1).padStart(2, '0')}</span>
                        <span className="hp-doc-article__title">{art.title}</span>
                        <FontAwesomeIcon icon={faChevronDown} className={`hp-doc-article__chevron ${isOpen ? 'hp-doc-article__chevron--open' : ''}`} />
                      </button>
                      <div className="hp-doc-article__body">
                        <p>{fullContent}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   API DOCS
   ════════════════════════════════════════════════ */
export const ApiDocsPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="API Documentation" />
    <div className="hp-info-page__hero">
      <h1>API <span className="gradient-text">Reference</span></h1>
      <p>RESTful API for programmatic access to ELVOAQ Vault.</p>
    </div>
    <div className="hp-info-page__content">
      <div className="hp-info-card">
        <h3><FontAwesomeIcon icon={faCode} /> REST API v2</h3>
        <p>
          Our API uses standard HTTP methods with JSON request/response bodies.
          All endpoints require authentication via Bearer token.
        </p>
        <div className="hp-info-code-block">
          <code>
            <span className="hp-code-method">GET</span> https://api.elvoaq.ch/v2/items<br />
            <span className="hp-code-method hp-code-method--post">POST</span> https://api.elvoaq.ch/v2/items<br />
            <span className="hp-code-method hp-code-method--put">PUT</span> https://api.elvoaq.ch/v2/items/:id<br />
            <span className="hp-code-method hp-code-method--del">DELETE</span> https://api.elvoaq.ch/v2/items/:id<br />
            <span className="hp-code-method">GET</span> https://api.elvoaq.ch/v2/categories<br />
            <span className="hp-code-method hp-code-method--post">POST</span> https://api.elvoaq.ch/v2/ai/categorize<br />
          </code>
        </div>
      </div>
      <div className="hp-info-card">
        <h3>Authentication</h3>
        <p>Include your API key in the Authorization header:</p>
        <div className="hp-info-code-block">
          <code>
            Authorization: Bearer YOUR_API_KEY<br />
            Content-Type: application/json
          </code>
        </div>
      </div>
      <div className="hp-info-grid hp-info-grid--2">
        <div className="hp-info-card">
          <h3>Rate Limits</h3>
          <div className="hp-info-facts">
            <div className="hp-info-fact"><strong>Free</strong><span>100 req/hour</span></div>
            <div className="hp-info-fact"><strong>Pro</strong><span>1,000 req/hour</span></div>
            <div className="hp-info-fact"><strong>Business</strong><span>10,000 req/hour</span></div>
            <div className="hp-info-fact"><strong>Enterprise</strong><span>Custom</span></div>
          </div>
        </div>
        <div className="hp-info-card">
          <h3>SDKs</h3>
          <p>Official SDKs for your favorite language:</p>
          <ul className="hp-info-list">
            <li><FontAwesomeIcon icon={faCheckCircle} /> JavaScript / TypeScript</li>
            <li><FontAwesomeIcon icon={faCheckCircle} /> Python</li>
            <li><FontAwesomeIcon icon={faCheckCircle} /> Go</li>
            <li>Ruby (Coming Soon)</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   BLOG  —  full article bodies, expandable
   ════════════════════════════════════════════════════════════════════ */

const BLOG_POSTS = [
  {
    date: 'Jan 28, 2026', title: 'Introducing ELVOAQ Vault 3.0', tag: 'Product', color: '#3B82F6',
    excerpt: 'A complete redesign with a new dark Swiss theme, AI integration, and enterprise features.',
    body: `After 6 months of intensive development, we're thrilled to launch ELVOAQ Vault 3.0 — the biggest update in our history.

**What's New:**
• **Complete Dark Swiss Theme** — A stunning redesign inspired by Swiss precision and elegance. Every pixel has been crafted for comfort during extended use, with a deep #06060e background and carefully chosen accent colors.
• **AI Integration** — We've integrated advanced AI directly into your workflow. Upload a photo and get instant categorization, tagging, and value estimation with 95%+ accuracy.
• **Enterprise Features** — Role-based access control (RBAC) with granular permissions, SSO support, advanced audit trails, and compliance-ready reporting for businesses of all sizes.
• **New Dashboard** — Customizable widgets let you build the perfect overview. Drag, drop, and resize to see exactly what matters most to your workflow.
• **Performance** — 3× faster search, 50% smaller bundle size, and offline-capable progressive web app functionality.

We've also refreshed our pricing — the Free tier now includes 500 items (up from 100) and basic AI categorization. The new Business tier at CHF 29.99/mo adds team features, audit logs, and API access.

Upgrade today at vault.elvoaq.ch and experience the future of item management.`,
  },
  {
    date: 'Jan 15, 2026', title: 'How We Use AI for Image Categorization', tag: 'Engineering', color: '#10B981',
    excerpt: 'A deep-dive into how we leverage advanced AI to power our 95%+ accurate auto-categorization engine.',
    body: `When users upload a photo to ELVOAQ Vault, something remarkable happens in 1-3 seconds: our system identifies the object, categorizes it, suggests relevant tags, and estimates its market value. Here's how it works under the hood.

**The Pipeline:**
1. **Image Processing** — Photos are resized and optimized client-side before upload. We extract EXIF data for metadata (date taken, camera model, GPS if available).
2. **AI Vision Analysis** — The optimized image is sent to our AI vision API. We use a carefully engineered prompt that asks the AI to identify the object, suggest a category from our taxonomy, list relevant attributes, and estimate value range.
3. **Post-Processing** — The AI response is parsed and matched against our category taxonomy (100+ categories, 500+ subcategories). We apply confidence scoring and only show suggestions above 70% confidence.
4. **Value Estimation** — We cross-reference the AI's identification with our market database of 2.5M+ comparable items to refine the value estimate.

**Accuracy Stats (January 2026):**
• Category accuracy: 95.3% (top-1), 99.1% (top-3)
• Value estimation: within 15% of actual market value for 85% of items
• Average processing time: 1.8 seconds

**Training & Improvement:** We don't fine-tune the AI model directly. Instead, user corrections feed into our post-processing layer — when users change a suggested category, we adjust our confidence weights and category mapping. This hybrid approach gives us the power of a frontier AI model with domain-specific accuracy.

The engineering team, led by Elena (AI/ML Engineer, ETH Zürich PhD), continues to push accuracy boundaries. Next milestone: 98% top-1 accuracy by Q3 2026.`,
  },
  {
    date: 'Jan 5, 2026', title: 'ELVOAQ AG — Now Officially Incorporated', tag: 'Company', color: '#8B5CF6',
    excerpt: 'We\'re proud to announce our incorporation as a Swiss AG in Zürich.',
    body: `Today marks a major milestone for ELVOAQ: we are now officially ELVOAQ AG, incorporated in Zürich, Switzerland under Swiss commercial law.

**What This Means:**
• **Legal Foundation** — As an AG (Aktiengesellschaft), ELVOAQ operates under one of the world's most respected corporate frameworks. This provides our customers with additional legal protections and accountability.
• **Data Protection** — Swiss corporate entities are bound by the Swiss Federal Act on Data Protection (DSG), which provides constitutional-level privacy guarantees. Your data is protected by both corporate law and privacy law.
• **Financial Stability** — AG incorporation requires minimum share capital and ongoing auditing requirements, demonstrating our commitment to long-term sustainability.
• **European Trust** — Switzerland's neutral position and strong rule of law make it the ideal home for a privacy-focused technology company.

**Our New Office:** We've also opened our first physical office near Paradeplatz in Zürich's financial district. While we remain a remote-first company, the office serves as a collaboration hub for our growing Swiss team.

**The Team:** With incorporation, we've grown to 15+ team members across 8 countries. Our core leadership — Lukas (CEO), Marian (CISO), and Susan (Lead Design) — drives the vision from Zürich.

Thank you to our 50,000+ users who believed in us from the beginning. ELVOAQ AG is just getting started.`,
  },
  {
    date: 'Dec 20, 2025', title: 'Swiss Data Protection in 2026', tag: 'Security', color: '#EF4444',
    excerpt: 'A comprehensive guide to the latest Swiss DSG regulations and how ELVOAQ ensures compliance.',
    body: `The revised Swiss Federal Act on Data Protection (revDSG) has been in effect since September 2023, and 2026 brings further enforcement clarifications. Here's what you need to know and how ELVOAQ stays ahead.

**Key Principles of the revDSG:**
• **Privacy by Design** — Data protection must be built into systems from the ground up, not bolted on as an afterthought. ELVOAQ Vault was architected with this principle from day one.
• **Data Minimization** — Collect only what's necessary. We store only the data you explicitly provide — no hidden tracking, no behavioral profiling, no data selling.
• **Transparency** — Users must know what data is collected and why. Our privacy dashboard shows exactly what we store and gives one-click export and deletion.
• **Breach Notification** — The FDPIC must be notified "as quickly as possible" of data breaches. We have automated breach detection with a target notification time of under 4 hours.

**How ELVOAQ Complies:**
• All data stored in Swiss Tier IV data centers (Zürich + Geneva)
• AES-256 encryption at rest, TLS 1.3 in transit
• Zero-knowledge password architecture
• Annual penetration testing by Swiss security firms
• ISO 27001 certification maintained since 2023
• Dedicated Data Protection Officer (privacy@elvoaq.ch)

**For Businesses:** If you use ELVOAQ Vault for business inventory, you benefit from our DSG compliance for your own regulatory obligations. We provide a Data Processing Agreement (DPA) for all Business and Enterprise customers.`,
  },
  {
    date: 'Dec 8, 2025', title: 'Building a Product People Love', tag: 'Design', color: '#F59E0B',
    excerpt: 'Our design philosophy: Swiss precision meets delightful user experience.',
    body: `Our Lead Designer Susan shares the design principles behind ELVOAQ Vault's interface — and why Swiss design heritage matters in software.

**Principle 1: Clarity Above All**
Swiss design is famous for its clarity — think of the Swiss railway clock, Helvetica typeface, or the iconic Swiss grid system. In Vault, every element has a purpose. No decorative clutter, no confusing layouts. The dark theme uses carefully calibrated contrast ratios that exceed WCAG AAA standards.

**Principle 2: Progressive Disclosure**
New users see a simple, welcoming interface. As they explore, advanced features reveal themselves naturally. The AI categorization suggestion appears only when you upload a photo. Bulk actions appear only when you select multiple items. This prevents overwhelm while keeping power at hand.

**Principle 3: Micro-Interactions Matter**
Every hover effect, transition, and animation is intentional. Cards lift slightly on hover (4px translateY). Expanded sections use 400ms ease transitions. Loading states show meaningful skeletons rather than spinners. These details compound into an experience that feels premium and polished.

**Principle 4: Accessibility is Non-Negotiable**
Vault supports keyboard navigation throughout, screen reader compatibility, reduced motion preferences, and high contrast mode. Color is never the only indicator — we always pair it with icons or text.

**The Result:** Our user satisfaction score is 4.9/5, and the average session length is 12 minutes — unusually high for an inventory tool. Users don't just use Vault; they enjoy using it.`,
  },
  {
    date: 'Nov 25, 2025', title: 'TimeCalc Preview: Smart Time Management', tag: 'Product', color: '#22D3EE',
    excerpt: 'A sneak peek at our upcoming time calculator product.',
    body: `ELVOAQ Vault is our flagship, but we've always planned to build a family of Swiss-quality tools. Today we're previewing the next member: ELVOAQ TimeCalc.

**What is TimeCalc?**
TimeCalc is an intelligent time calculator designed for professionals, freelancers, and teams who work across time zones, bill by the hour, or need precise scheduling.

**Planned Features:**
• **Multi-Timezone Calculator** — Instantly convert meetings across any number of time zones. See availability overlap for global teams with visual timeline bars.
• **Work Hour Tracker** — Log billable hours with one-click start/stop timers. Auto-detect breaks. Generate invoicing-ready reports with client breakdowns.
• **Smart Scheduling** — Input constraints ("meeting must be between 9-17 CET and 8-16 EST") and get optimal time suggestions. Integrates with Google Calendar and Outlook.
• **Project Time Budgets** — Set time budgets per project and track burn rate. Get alerts when approaching limits. Compare estimated vs actual hours across sprints.
• **Swiss Precision** — Time calculations accurate to the millisecond. Full compliance with Swiss labor law time-tracking requirements.

**Timeline:** Private beta opens Q2 2026 for existing ELVOAQ Vault users. Public launch planned for Q3 2026. Pricing will be announced closer to launch, with bundle discounts for users of both Vault and TimeCalc.

Sign up for early access at elvoaq.ch/timecalc-preview.`,
  },
];

export const BlogPage = () => {
  const [expandedPost, setExpandedPost] = useState<number | null>(null);

  return (
    <div className="hp hp-info-page">
      <PageNav title="Blog" />
      <div className="hp-info-page__hero">
        <h1>ELVOAQ <span className="gradient-text">Blog</span></h1>
        <p>Product updates, engineering insights, and industry news.</p>
      </div>
      <div className="hp-info-page__content">
        <div className="hp-blog-list">
          {BLOG_POSTS.map((post, i) => (
            <div className={`hp-blog-post ${expandedPost === i ? 'hp-blog-post--open' : ''}`} key={i}>
              <div className="hp-blog-post__header">
                <div className="hp-blog-post__meta">
                  <span className="hp-info-card__tag" style={{ background: `${post.color}20`, color: post.color }}>{post.tag}</span>
                  <span className="hp-blog-post__date">{post.date}</span>
                </div>
                <h3>{post.title}</h3>
                <p className="hp-blog-post__excerpt">{post.excerpt}</p>
                <button className="hp-info-card__link" onClick={() => setExpandedPost(expandedPost === i ? null : i)}>
                  {expandedPost === i ? 'Close Article' : 'Read Article'}{' '}
                  <FontAwesomeIcon icon={expandedPost === i ? faChevronDown : faArrowRight} />
                </button>
              </div>
              <div className="hp-blog-post__body">
                {post.body.split('\n\n').map((para, k) => (
                  <p key={k} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/^• /gm, '').replace(/\n• /g, '<br/>• ') }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   HELP CENTER  —  expandable FAQ with real answers
   ════════════════════════════════════════════════════════════════════ */

const HELP_CATEGORIES = [
  {
    title: 'Account & Billing',
    faqs: [
      { q: 'How do I reset my password?', a: 'Go to the login page and click "Forgot Password". Enter the email associated with your account. You\'ll receive a reset link within 60 seconds. The link expires after 1 hour. If you have 2FA enabled, you\'ll need to verify with your authenticator app after setting the new password.' },
      { q: 'How do I upgrade my plan?', a: 'Go to Settings → Subscription → Upgrade. Choose your new plan (Pro, Business, or Enterprise). Payment is processed via Stripe using your credit card, debit card, or SEPA direct debit. The upgrade takes effect immediately, and you\'re only charged the prorated difference for the current billing period.' },
      { q: 'Can I cancel anytime?', a: 'Yes, you can cancel your subscription at any time from Settings → Subscription → Cancel. Your account will remain active until the end of your current billing period. After cancellation, your account downgrades to the Free tier — you keep all your items, but features are limited to Free plan capabilities. You can re-subscribe anytime.' },
      { q: 'How do I enable 2FA?', a: 'Go to Settings → Security → Two-Factor Authentication → Enable. Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.). Enter the 6-digit code to verify setup. Save your backup codes in a secure location — these allow you to regain access if you lose your authenticator device.' },
      { q: 'What payment methods are accepted?', a: 'We accept Visa, Mastercard, American Express, and SEPA direct debit. All payments are processed securely through Stripe. We also support annual billing (2 months free). Enterprise customers can pay via bank transfer/invoice. All prices are in Swiss Francs (CHF).' },
      { q: 'How do I get an invoice?', a: 'Invoices are automatically generated for every payment and sent to your account email. You can also download all past invoices from Settings → Subscription → Billing History. Invoices include your company details if you\'ve added them in Settings → Profile → Company Information.' },
    ],
  },
  {
    title: 'Items & Categories',
    faqs: [
      { q: 'How do I add an item?', a: 'Click the "+" button on the Dashboard or Library page, or press Ctrl+N (Cmd+N on Mac). Upload one or more photos — our AI will automatically suggest a category, tags, and estimated value. Fill in any additional details (name, description, purchase date, condition) and click Save. Items are instantly searchable and appear in your Library.' },
      { q: 'How does AI categorization work?', a: 'When you upload a photo, it\'s sent to our AI engine for analysis. The AI identifies the object, suggests a category from our taxonomy of 100+ categories and 500+ subcategories, adds relevant tags, and estimates market value. The process takes 1-3 seconds and achieves 95%+ accuracy. You can always edit suggestions — corrections help improve future recommendations.' },
      { q: 'Can I import from CSV?', a: 'Yes! Go to Settings → Import/Export → Import. Upload a CSV, JSON, or Excel file. Our visual column mapper lets you match your file\'s columns to ELVOAQ Vault fields. Preview the import before confirming. We support up to 10,000 items per import. For larger imports, contact support or use our API\'s batch endpoint.' },
      { q: 'How do I bulk edit items?', a: 'In the Library view, click the checkbox icon in the toolbar to enter selection mode. Select items individually or use "Select All" / filter-based selection. The bulk action bar appears at the bottom with options: Move to Category, Add Tags, Update Condition, Change Location, Export, and Delete. Changes are applied instantly to all selected items.' },
      { q: 'What\'s the difference between categories and tags?', a: 'Categories are hierarchical (Electronics → Cameras → DSLR) — each item belongs to exactly one category. Tags are flat and flexible — an item can have unlimited tags ("vintage", "gift", "insured", "office"). Use categories for primary organization and tags for cross-cutting attributes.' },
      { q: 'How do I track warranty dates?', a: 'When adding or editing an item, fill in the "Warranty Expiry" field with the expiry date. ELVOAQ Vault will automatically send you a notification 30 days before expiry (configurable in notification settings). You can also upload warranty documents as photos attached to the item.' },
    ],
  },
  {
    title: 'Teams & Workspaces',
    faqs: [
      { q: 'How do I invite team members?', a: 'In Workspace Settings → Members, click "Invite Member". Enter their email address and select a role (Viewer, Editor, Manager, or Admin). They\'ll receive an email invitation with a link to join. If they don\'t have an ELVOAQ account, they can create one during the invitation acceptance flow. You can invite up to 10 people at once.' },
      { q: 'What are workspace roles?', a: 'Viewer: Can browse and search items, view photos and comments, but cannot modify anything. Editor: Can add, edit, and delete items, upload photos, and leave comments. Manager: All Editor permissions plus managing members, categories, and workspace settings. Admin: Full control including billing, audit logs, API keys, and workspace deletion.' },
      { q: 'Can I transfer ownership?', a: 'Yes. Go to Workspace Settings → General → Transfer Ownership. Select the new owner (must be an existing Admin member). You\'ll need to confirm with your password and 2FA code. After transfer, your role changes to Admin (not owner). Only one owner exists per workspace.' },
      { q: 'How do I set up SSO?', a: 'SSO is available on Business and Enterprise plans. Go to Workspace Settings → Security → Single Sign-On. We support SAML 2.0 and OIDC providers (Okta, Azure AD, Google Workspace, etc.). Upload your IdP metadata or enter the configuration URLs. Once configured, team members can sign in with their corporate credentials.' },
    ],
  },
  {
    title: 'Privacy & Security',
    faqs: [
      { q: 'Where is my data stored?', a: 'All data is stored exclusively in Tier IV certified data centers located in Zürich and Geneva, Switzerland. We do not replicate data outside of Swiss borders. Our infrastructure runs on dedicated servers managed by our DevOps team — not shared public cloud. Switzerland\'s constitutional privacy protections and the Swiss DSG law provide the highest level of data protection.' },
      { q: 'Is my data encrypted?', a: 'Yes, comprehensively. At rest: AES-256 encryption for all stored data, including database records and uploaded files. In transit: TLS 1.3 for all connections between your browser and our servers. Application-level: Sensitive fields (values, notes) use additional application-layer encryption. Passwords: bcrypt with salt — we use zero-knowledge architecture and never see your plaintext password.' },
      { q: 'How do I export my data?', a: 'Go to Settings → Import/Export → Export. Choose format: JSON (complete, machine-readable, ideal for migration), CSV (spreadsheet-compatible), or PDF (printable summary reports). Select what to export: All Items, Specific Workspaces, or Filtered Results. The export includes all metadata, category assignments, tags, values, comments, and photo URLs. Large exports are processed in the background and emailed when ready.' },
      { q: 'What is your data retention policy?', a: 'Active accounts: Data is retained as long as your account is active. Deleted items: Moved to Trash for 30 days (recoverable), then permanently deleted. Account deletion: All data is permanently removed within 30 days, including from backups. Audit logs: Retained for 7 years on Business/Enterprise plans (regulatory compliance). We never sell, share, or use your data for advertising.' },
    ],
  },
  {
    title: 'Photos & Storage',
    faqs: [
      { q: 'What formats are supported?', a: 'We support JPEG, PNG, WebP, and HEIC (iPhone) formats. Maximum file size is 20 MB per photo. Photos are automatically optimized for web viewing while preserving the original file in storage. For best results, we recommend JPEG at high quality or WebP for smaller file sizes.' },
      { q: 'How many photos per item?', a: 'Each item supports up to 20 photos. Drag and drop to reorder them. The first photo becomes the item\'s thumbnail in list and grid views. You can zoom into any photo and use the full-screen viewer with left/right navigation.' },
      { q: 'How do I manage storage?', a: 'Check your storage usage in Settings → Subscription → Storage. The bar shows used vs. available space. To free up space: delete items you no longer need, remove duplicate photos, or compress existing images (Settings → Storage → Optimize). Need more space? Contact us at support@elvoaq.ch and we\'ll set up a custom storage package for you, or upgrade your plan.' },
      { q: 'Can I organize with galleries?', a: 'Yes! Create galleries within a workspace to group related photos. Go to Library → Galleries → Create Gallery. Add items to galleries by selecting them and choosing "Add to Gallery". Galleries can be shared with read-only links — perfect for showing collections to insurance agents, appraisers, or family members.' },
    ],
  },
  {
    title: 'API & Integrations',
    faqs: [
      { q: 'How do I get an API key?', a: 'Go to Settings → API → Generate Key. Name your key (e.g., "Zapier Integration") and select its scope: Read Only, Read/Write, or Admin. The key is shown once — copy and store it securely. You can rotate keys anytime without downtime. We recommend separate keys for each integration.' },
      { q: 'What integrations exist?', a: 'Currently available: Slack (real-time notifications), Zapier (connect to 5,000+ apps), Google Sheets (inventory sync), REST API (custom integrations), and Webhooks (real-time events). Coming soon: Xero/QuickBooks (accounting sync), Microsoft Power Automate, and a native Notion integration.' },
      { q: 'Is there a webhook system?', a: 'Yes! Configure webhooks in Settings → API → Webhooks. Add a URL and select events: item.created, item.updated, item.deleted, member.joined, comment.added, value.changed. Each payload is signed with HMAC-SHA256 for verification. Failed deliveries are retried 3 times with exponential backoff. Test with our built-in webhook simulator.' },
      { q: 'How do I use the SDK?', a: 'Install our official SDK: `npm install @elvoaq/sdk` (JavaScript/TypeScript), `pip install elvoaq` (Python), or `go get github.com/elvoaq/sdk-go` (Go). Initialize with your API key and start making calls. Full SDK documentation with examples is available at docs.elvoaq.ch/sdk. The SDK handles authentication, pagination, rate limiting, and retries automatically.' },
    ],
  },
];

export const HelpPage = () => {
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFaq = (key: string) => setExpandedFaq(expandedFaq === key ? null : key);

  const filteredCategories = searchTerm.trim()
    ? HELP_CATEGORIES.map(cat => ({
      ...cat,
      faqs: cat.faqs.filter(f =>
        f.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.a.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    })).filter(cat => cat.faqs.length > 0)
    : HELP_CATEGORIES;

  return (
    <div className="hp hp-info-page">
      <PageNav title="Help Center" />
      <div className="hp-info-page__hero">
        <h1>Help <span className="gradient-text">Center</span></h1>
        <p>Find answers to common questions or reach out to our support team.</p>
      </div>
      <div className="hp-info-page__content">
        <div className="hp-info-card">
          <h3><FontAwesomeIcon icon={faHeadset} /> How can we help?</h3>
          <div className="hp-info-search">
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredCategories.map((cat, ci) => (
          <div className="hp-help-category" key={ci}>
            <h3 className="hp-help-category__title">{cat.title}</h3>
            <div className="hp-help-faqs">
              {cat.faqs.map((faq, fi) => {
                const key = `${ci}-${fi}`;
                const isOpen = expandedFaq === key;
                return (
                  <div className={`hp-help-faq ${isOpen ? 'hp-help-faq--open' : ''}`} key={fi}>
                    <button className="hp-help-faq__question" onClick={() => toggleFaq(key)}>
                      <span>{faq.q}</span>
                      <FontAwesomeIcon icon={faChevronDown} className={`hp-help-faq__chevron ${isOpen ? 'hp-help-faq__chevron--open' : ''}`} />
                    </button>
                    <div className="hp-help-faq__answer">
                      <p>{faq.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="hp-info-card" style={{ textAlign: 'center', marginTop: 32 }}>
            <h3>No results found</h3>
            <p>Try different keywords or contact our support team directly.</p>
          </div>
        )}

        <div className="hp-info-card" style={{ textAlign: 'center', marginTop: 40 }}>
          <h3>Still need help?</h3>
          <p>Our support team in Zürich responds within 4 hours during business hours.</p>
          <p><strong>support@elvoaq.ch</strong> · Mon–Fri, 9:00–18:00 CET</p>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   STATUS
   ════════════════════════════════════════════════ */
export const StatusPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="System Status" />
    <div className="hp-info-page__hero">
      <h1>System <span className="gradient-text">Status</span></h1>
      <p>Real-time status of all ELVOAQ services.</p>
    </div>
    <div className="hp-info-page__content">
      <div className="hp-info-card hp-info-card--status-overall">
        <div className="hp-status-indicator hp-status-indicator--ok" />
        <div>
          <h3>All Systems Operational</h3>
          <p>Last updated: {new Date().toLocaleString()}</p>
        </div>
      </div>
      <div className="hp-status-grid">
        {[
          { name: 'ELVOAQ Vault — Web App', status: 'Operational', uptime: '99.98 %' },
          { name: 'ELVOAQ Vault — API', status: 'Operational', uptime: '99.99 %' },
          { name: 'AI Integration', status: 'Operational', uptime: '99.90 %' },
          { name: 'File Storage (Swiss DC)', status: 'Operational', uptime: '99.99 %' },
          { name: 'Authentication Service', status: 'Operational', uptime: '100 %' },
          { name: 'Email & Notifications', status: 'Operational', uptime: '99.95 %' },
          { name: 'Database Cluster', status: 'Operational', uptime: '99.99 %' },
          { name: 'CDN & Static Assets', status: 'Operational', uptime: '99.97 %' },
        ].map((svc, i) => (
          <div className="hp-status-row" key={i}>
            <div className="hp-status-row__name">
              <div className="hp-status-dot hp-status-dot--ok" />
              {svc.name}
            </div>
            <span className="hp-status-row__status">{svc.status}</span>
            <span className="hp-status-row__uptime">{svc.uptime}</span>
          </div>
        ))}
      </div>
      <div className="hp-info-card" style={{ marginTop: 32 }}>
        <h3>Recent Incidents</h3>
        <div className="hp-incident">
          <span className="hp-incident__date">Jan 20, 2026</span>
          <div>
            <strong>Brief API latency increase</strong>
            <p>Resolved within 12 minutes. Root cause: CDN cache invalidation during deployment.</p>
          </div>
        </div>
        <div className="hp-incident">
          <span className="hp-incident__date">Jan 8, 2026</span>
          <div>
            <strong>Scheduled maintenance — database upgrade</strong>
            <p>Planned 15-minute downtime window completed successfully.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════
   INTEGRATIONS
   ════════════════════════════════════════════════ */
export const IntegrationsPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Integrations" />
    <div className="hp-info-page__hero">
      <h1><span className="gradient-text">Integrations</span></h1>
      <p>Connect ELVOAQ Vault with your favorite tools and services.</p>
    </div>
    <div className="hp-info-page__content">
      <div className="hp-info-grid hp-info-grid--3">
        {[
          { name: 'Slack', desc: 'Get notifications about item changes and team updates directly in Slack.', status: 'Available', color: '#E01E5A' },
          { name: 'Zapier', desc: 'Connect Vault to 5,000+ apps with no-code automation workflows.', status: 'Available', color: '#FF4A00' },
          { name: 'Google Sheets', desc: 'Sync your inventory data to Google Sheets for custom reporting.', status: 'Available', color: '#34A853' },
          { name: 'Xero / QuickBooks', desc: 'Sync asset values and depreciation data to your accounting software.', status: 'Coming Soon', color: '#13B5EA' },
          { name: 'REST API', desc: 'Build custom integrations with our comprehensive REST API.', status: 'Available', color: '#3B82F6' },
          { name: 'Webhooks', desc: 'Receive real-time event notifications via configurable webhooks.', status: 'Available', color: '#8B5CF6' },
        ].map((int, i) => (
          <div className="hp-info-card" key={i}>
            <div className="hp-info-card__integration-header">
              <div className="hp-info-card__icon" style={{ background: `${int.color}15`, color: int.color }}>
                <FontAwesomeIcon icon={faPuzzlePiece} />
              </div>
              <span className={`hp-info-card__status ${int.status === 'Available' ? 'hp-info-card__status--ok' : ''}`}>{int.status}</span>
            </div>
            <h3>{int.name}</h3>
            <p>{int.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════════════
   PRIVACY POLICY
   ════════════════════════════════════════════════ */
export const PrivacyPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Privacy Policy" />
    <div className="hp-info-page__hero">
      <h1>Privacy <span className="gradient-text">Policy</span></h1>
      <p>Effective Date: 19 February 2026 · Governing Law: Swiss Federal Act on Data Protection (nFADP / revDSG) · Canton of Zug, Switzerland</p>
    </div>
    <div className="hp-info-page__content hp-info-page__content--text">

      <div className="hp-info-card">
        <h3>1. Introduction and Scope</h3>
        <p>
          Elvoaq AG ("we", "us", "our", "Elvoaq") is committed to protecting your privacy and the security of your personal data
          with the highest standards. This Privacy Policy ("Policy") explains how we collect, process, use, store, share, and
          protect personal data in connection with your use of the Elvoaq platform, websites, applications, APIs, and related
          services (collectively, the "Platform").
        </p>
        <p>This Policy applies to all users of the Platform ("you", "your") and is designed to comply with the following legal frameworks:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Swiss Federal Act on Data Protection (revDSG / nFADP), effective 1 September 2023</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Swiss Data Protection Ordinance (DSV / DPO)</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Regulation (EU) 2016/679 (GDPR) to the extent applicable to users in the EEA</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> UK General Data Protection Regulation (UK GDPR) to the extent applicable</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Any other applicable data protection laws of relevant jurisdictions</li>
        </ul>
        <p>If you do not agree to this Policy, you must not use the Platform. Your continued use constitutes acceptance of this Policy and any amendments thereto.</p>
      </div>

      <div className="hp-info-card">
        <h3>2. Identity of the Data Controller</h3>
        <p>The data controller responsible for the processing of your personal data is:</p>
        <p>
          <strong>Elvoaq AG</strong><br />
          Canton of Zug, Switzerland<br />
          Data Protection Officer (DPO): <strong>dpo@elvoaq.com</strong><br />
          Privacy Inquiries: <strong>privacy@elvoaq.com</strong>
        </p>
        <p>Elvoaq AG is registered in Switzerland and operates under Swiss law. For users in the EEA or UK, Elvoaq AG may, where required, designate a representative in those jurisdictions. Contact legal@elvoaq.com for representative details.</p>
      </div>

      <div className="hp-info-card">
        <h3>3. Personal Data We Collect</h3>
        <p><strong>3.1 Data You Provide Directly</strong></p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Identity and contact data: full name, email address, telephone number, postal address, and date of birth</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Account credentials: username, hashed password, and authentication tokens</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Profile data: biography, profile photograph, preferences, and settings</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Communications: messages, support tickets, feedback, survey responses</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Payment data: billing address and payment method details (processed by PCI-DSS-compliant processors; we do not store full card numbers)</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Identity verification documents where required by law</li>
        </ul>
        <p><strong>3.2 Data Collected Automatically</strong></p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Technical data: IP address, device identifiers, browser type, operating system, hardware model, network information</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Usage data: pages and features accessed, actions taken, timestamps, session duration, click-stream data, referral URLs</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Log data: server logs, error reports, crash data, diagnostic information</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Cookies and similar technologies as described in Section 9</li>
        </ul>
        <p><strong>3.3 Data from Third Parties</strong> — We may receive personal data from identity/authentication providers, business partners, and publicly available sources to the extent permitted by law.</p>
        <p><strong>3.4 Special Categories of Data</strong> — We do not intentionally collect sensitive personal data (health, racial origin, political opinions, religious beliefs, biometric data, etc.). You are advised not to include such data in your use of the Platform.</p>
      </div>

      <div className="hp-info-card">
        <h3>4. Legal Basis for Processing</h3>
        <p>Under the nFADP (Switzerland) and GDPR (EU/EEA), we process your personal data on the following legal bases:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Contract performance:</strong> processing necessary for providing access to the Platform and managing your Account</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Legitimate interests:</strong> operating and improving the Platform, preventing fraud and abuse, ensuring security, business analytics</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Legal obligation:</strong> compliance with Swiss law or other applicable law, including anti-money-laundering regulations, tax obligations</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Consent:</strong> where we rely on your consent (e.g., direct marketing, certain cookies) — you may withdraw consent at any time</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Vital interests:</strong> in rare emergency circumstances where processing is necessary to protect the vital interests of a person</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>5. Purposes of Processing</h3>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Account creation and management</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Service provision, operation, maintenance, and improvement</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Communications: transactional messages, security alerts, technical notices</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Customer support: inquiries, disputes, and troubleshooting</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Security and fraud prevention</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Legal compliance</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Analytics and improvement</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Marketing (with consent) — you may opt out at any time</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Dispute resolution and business transfers</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>6. Disclosure of Personal Data</h3>
        <p>We do not sell, rent, or trade your personal data to third parties for their marketing purposes. We disclose your data only as described in this Policy.</p>
        <p><strong>6.2 Service Providers</strong> — Carefully selected third-party service providers processing data on our behalf under binding data processing agreements, including: cloud hosting (Switzerland/EEA-based where practicable), payment processors, email providers, analytics providers, security providers, and legal advisors.</p>
        <p><strong>6.3 Legal Requirements</strong> — We may disclose data when necessary to comply with legal obligations, enforce Terms, protect rights, property, or safety, or prevent illegal activity.</p>
        <p><strong>6.6 No Sale of Data</strong> — Elvoaq AG DOES NOT SELL, MONETISE, OR COMMERCIALLY EXPLOIT YOUR PERSONAL DATA FOR THE BENEFIT OF THIRD-PARTY ADVERTISERS OR DATA BROKERS.</p>
      </div>

      <div className="hp-info-card">
        <h3>7. International Data Transfers</h3>
        <p>Your personal data is primarily stored and processed in Switzerland, which is recognised by the EU as providing an adequate level of data protection. Where we transfer personal data outside Switzerland or the EEA, we implement appropriate safeguards including:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Standard Contractual Clauses (SCCs) approved by the European Commission or the Swiss FDPIC</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Binding corporate rules, where applicable</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Other transfer mechanisms recognised under applicable data protection law</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>8. Data Retention</h3>
        <p>We retain personal data for as long as necessary to fulfil the purposes for which it was collected, and as required by law. Retention criteria include:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Duration of your Account and contractual relationship</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Legal and regulatory retention obligations (e.g., financial records up to ten years under Swiss law)</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Statute of limitations for potential legal claims</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Legitimate interests in operating a secure, reliable service</li>
        </ul>
        <p>After the applicable retention period expires, personal data is securely deleted or anonymised.</p>
      </div>

      <div className="hp-info-card">
        <h3>9. Cookies and Similar Technologies</h3>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Strictly necessary cookies:</strong> essential for operation (authentication, session management) — cannot be disabled</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Performance and analytics cookies:</strong> help us understand usage patterns — may include anonymised analytics data</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Functionality cookies:</strong> remember your preferences and settings</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Targeting and marketing cookies:</strong> only placed with your prior consent</li>
        </ul>
        <p>You can manage your cookie preferences through your browser settings or through the Platform's cookie consent management tool.</p>
      </div>

      <div className="hp-info-card">
        <h3>10. Your Data Subject Rights</h3>
        <p>Subject to applicable law, you have the following rights:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right of access:</strong> obtain confirmation and a copy of your personal data</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to rectification:</strong> request correction of inaccurate or incomplete data</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to erasure ("right to be forgotten"):</strong> request deletion in certain circumstances</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to restriction of processing:</strong> request restriction while we verify contested data</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to data portability:</strong> receive data in a structured, commonly used, machine-readable format</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to object:</strong> object to processing based on legitimate interests; absolute right to object to direct marketing</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right to withdraw consent:</strong> withdraw consent at any time without affecting prior lawfulness</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Right not to be subject to automated decision-making:</strong> not be subject to solely automated profiling with legal effects</li>
        </ul>
        <p>To exercise your rights, contact <strong>privacy@elvoaq.com</strong>. We will respond within thirty (30) days. You also have the right to lodge a complaint with the Swiss Federal Data Protection and Information Commissioner (FDPIC) at www.edoeb.admin.ch.</p>
      </div>

      <div className="hp-info-card">
        <h3>11. Data Security</h3>
        <p>We implement comprehensive technical and organisational security measures:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Encryption in transit (TLS/SSL) and at rest using industry-standard algorithms</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Access controls, role-based access management, and principle of least privilege</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Multi-factor authentication for administrative access</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Regular security assessments, vulnerability scanning, and penetration testing</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Employee training on data protection and security practices</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Incident response and data breach notification procedures compliant with nFADP and GDPR</li>
        </ul>
        <p>No method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>
      </div>

      <div className="hp-info-card">
        <h3>12. Children's Privacy</h3>
        <p>The Platform is not directed at persons under eighteen (18) years of age. We do not knowingly collect personal data from minors. If we become aware of inadvertent collection, we will promptly delete such data. Contact <strong>privacy@elvoaq.com</strong> if you believe a minor's data has been collected.</p>
      </div>

      <div className="hp-info-card">
        <h3>13. Third-Party Websites and Services</h3>
        <p>The Platform may contain links to third-party services. This Policy does not apply to the privacy practices of those third parties. We encourage you to review their privacy policies.</p>
      </div>

      <div className="hp-info-card">
        <h3>14. Changes to This Privacy Policy</h3>
        <p>We may update this Policy to reflect changes in practices, technology, or legal requirements. We will notify you of material changes by updating the Effective Date and, where practicable, by email or Platform notification. Continued use after the effective date constitutes acceptance.</p>
      </div>

      <div className="hp-info-card">
        <h3>15. Swiss-Specific Provisions</h3>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Subject to oversight by the Federal Data Protection and Information Commissioner (FDPIC)</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Internal register of data processing activities maintained per nFADP</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Transparency and consent for automated profiling with legal effects</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Privacy by Design and by Default in accordance with Article 7 nFADP</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>16. Contact Information</h3>
        <p>
          <strong>Elvoaq AG</strong> — Data Protection Officer<br />
          Email (DPO): <strong>dpo@elvoaq.com</strong><br />
          Email (Privacy): <strong>privacy@elvoaq.com</strong><br />
          Canton of Zug, Switzerland
        </p>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '16px' }}>© 2026 Elvoaq AG. All rights reserved. This Privacy Policy was last updated: 19 February 2026.</p>
      </div>

    </div>
  </div>
);

/* ════════════════════════════════════════════════
   TERMS OF USE
   ════════════════════════════════════════════════ */
export const TermsPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Terms of Use" />
    <div className="hp-info-page__hero">
      <h1>Terms of <span className="gradient-text">Use</span></h1>
      <p>Effective Date: 19 February 2026 · Governing Law: Swiss Law · Jurisdiction: Canton of Zug, Switzerland</p>
    </div>
    <div className="hp-info-page__content hp-info-page__content--text">

      <div className="hp-info-card">
        <h3>Preamble and Acceptance</h3>
        <p>
          PLEASE READ THESE TERMS OF USE ("TERMS", "AGREEMENT") CAREFULLY BEFORE ACCESSING OR USING THE ELVOAQ PLATFORM,
          SERVICES, APPLICATIONS, APIS, WEBSITES, OR ANY RELATED PRODUCTS OR SERVICES (COLLECTIVELY, THE "PLATFORM") OPERATED
          BY ELVOAQ AG ("COMPANY", "WE", "US", "OUR"), A COMPANY INCORPORATED UNDER THE LAWS OF SWITZERLAND.
        </p>
        <p>
          BY ACCESSING OR USING THE PLATFORM IN ANY MANNER WHATSOEVER, YOU ("USER", "YOU", "YOUR") UNCONDITIONALLY ACCEPT AND
          AGREE TO BE BOUND BY THESE TERMS IN THEIR ENTIRETY. IF YOU DO NOT AGREE TO ALL OF THESE TERMS, YOU MUST IMMEDIATELY
          CEASE USE OF THE PLATFORM AND DELETE ANY ASSOCIATED ACCOUNTS OR SOFTWARE.
        </p>
      </div>

      <div className="hp-info-card">
        <h3>1. Definitions</h3>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"Platform"</strong> — the Elvoaq software platform, including all websites, mobile applications, desktop applications, APIs, developer tools, content, services, and features</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"Content"</strong> — any text, data, information, files, images, audio, video, code, or other material on or through the Platform</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"User Content"</strong> — any Content submitted, posted, or otherwise provided by a User</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"Account"</strong> — the registered account created by a User to access the Platform</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"Intellectual Property"</strong> — all patents, trademarks, service marks, trade names, domain names, copyrights, trade secrets, know-how, software, databases, and all other proprietary rights</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>"Third-Party Services"</strong> — any services, content, or platforms operated by parties other than Elvoaq AG</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>2. Eligibility and Registration</h3>
        <p><strong>2.1 Age Requirements</strong> — The Platform is available only to persons who are at least eighteen (18) years of age. By using the Platform, you represent and warrant that you are at least eighteen (18) years old and have full legal capacity to enter into these Terms.</p>
        <p><strong>2.2 Account Registration</strong> — You agree to provide accurate, current, and complete information during registration and to maintain its accuracy. You are solely responsible for maintaining the confidentiality of your Account credentials.</p>
        <p><strong>2.3 Account Security</strong> — You are entirely responsible for all activities under your Account. You must notify Elvoaq AG immediately at legal@elvoaq.com upon becoming aware of any unauthorised access.</p>
        <p><strong>2.4 Account Termination by Company</strong> — Elvoaq AG reserves the absolute right, at its sole discretion, without prior notice and without liability, to suspend, restrict, or permanently terminate your Account for any reason.</p>
      </div>

      <div className="hp-info-card">
        <h3>3. Permitted Use and Restrictions</h3>
        <p><strong>3.1 Licence Grant</strong> — Subject to compliance with these Terms, Elvoaq AG grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable licence to access and use the Platform solely for your personal or internal business purposes.</p>
        <p><strong>3.2 Prohibited Conduct</strong> — You agree that you will not, under any circumstances:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> Violate any applicable law, regulation, treaty, or convention</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Use the Platform for any unlawful, fraudulent, deceptive, or malicious purpose</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Upload any Prohibited Content (illegal, harmful, threatening, abusive, defamatory, obscene, etc.)</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Attempt to gain unauthorised access to the Platform, other Accounts, or connected systems</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Engage in automated data collection, scraping, or mining without express written permission</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Introduce viruses, trojans, worms, logic bombs, or other malicious material</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Reverse engineer, decompile, or attempt to derive source code of any Platform component</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Circumvent, disable, or interfere with any security or access control features</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Impersonate any person or entity, or engage in identity theft, financial fraud, or money laundering</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>4. User Content</h3>
        <p><strong>4.1 Ownership</strong> — You retain all ownership rights in your User Content, subject to the licence granted herein.</p>
        <p><strong>4.2 Licence to Company</strong> — By submitting User Content, you grant Elvoaq AG a worldwide, non-exclusive, royalty-free, sublicensable, and transferable licence to use, host, store, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, and display such Content for operating, improving, and promoting the Platform.</p>
        <p><strong>4.3 Representations</strong> — You represent that: (a) you own or control all rights; (b) your Content does not violate any law or third-party rights; (c) it is not false, misleading, or deceptive; (d) it contains no Prohibited Content. YOU ARE SOLELY RESPONSIBLE FOR YOUR USER CONTENT.</p>
        <p><strong>4.4 Content Moderation</strong> — Elvoaq AG reserves the absolute right to review, screen, refuse, remove, or disable access to any User Content without prior notice.</p>
      </div>

      <div className="hp-info-card">
        <h3>5. Intellectual Property</h3>
        <p><strong>5.1 Company IP</strong> — The Platform and all components (software, code, design, text, graphics, logos, trademarks) are the exclusive intellectual property of Elvoaq AG or its licensors, protected by Swiss and international intellectual property laws.</p>
        <p><strong>5.2 Feedback</strong> — If you provide feedback, suggestions, or ideas, you assign all rights therein to Elvoaq AG, which may use and exploit such feedback without restriction or compensation.</p>
        <p><strong>5.3 Infringement Claims</strong> — Contact legal@elvoaq.com with details of any alleged intellectual property infringement.</p>
      </div>

      <div className="hp-info-card">
        <h3>6. Disclaimer of Warranties</h3>
        <p>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
          ANY WARRANTIES OF ANY KIND. ELVOAQ AG EXPRESSLY DISCLAIMS ALL WARRANTIES, INCLUDING: implied warranties of merchantability,
          fitness for a particular purpose, title, non-infringement; any warranty that the Platform will be uninterrupted, error-free,
          secure, or virus-free; any warranty as to accuracy, completeness, or reliability of any content. NO ADVICE OR INFORMATION
          FROM ELVOAQ AG SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN.
        </p>
      </div>

      <div className="hp-info-card">
        <h3>7. Limitation of Liability</h3>
        <p><strong>7.1 Exclusion of Consequential Damages</strong> — In no event shall Elvoaq AG be liable for any indirect, incidental, special, consequential, punitive, or exemplary damages, including loss of profits, revenue, data, goodwill, or business opportunity.</p>
        <p><strong>7.2 Aggregate Cap</strong> — Total cumulative liability shall not exceed the greater of (A) the amount you paid in the preceding twelve (12) months, or (B) one hundred Swiss Francs (CHF 100).</p>
        <p><strong>7.3 Specific Exclusions</strong> — Elvoaq AG assumes no liability for: actions of third parties; loss or corruption of data; unauthorised access; bugs or viruses transmitted by third parties; errors or omissions in content; interruption, suspension, or discontinuation of the Platform; or force majeure events.</p>
      </div>

      <div className="hp-info-card">
        <h3>8. Indemnification</h3>
        <p>You shall defend, indemnify, and hold harmless Elvoaq AG and its affiliates, directors, officers, employees, contractors, licensors, agents, and successors from any claims, damages, losses, liabilities, costs, and expenses arising out of: your use of the Platform; your User Content; your violation of these Terms or any applicable law; your violation of third-party rights; any dispute between you and a third party.</p>
      </div>

      <div className="hp-info-card">
        <h3>9. Third-Party Services and Links</h3>
        <p>The Platform may contain links to or integrations with Third-Party Services. Elvoaq AG has no control over and assumes no responsibility for third-party content, privacy policies, or practices. You access Third-Party Services at your own risk.</p>
      </div>

      <div className="hp-info-card">
        <h3>10. Availability and Modifications</h3>
        <p>Elvoaq AG makes no commitment regarding availability, continuity, or uptime. Elvoaq AG reserves the right to modify, suspend, or discontinue the Platform at any time, without notice and without liability.</p>
      </div>

      <div className="hp-info-card">
        <h3>11. Governing Law and Dispute Resolution</h3>
        <p><strong>11.1 Governing Law</strong> — These Terms are exclusively governed by the laws of Switzerland, without regard to conflict of law principles.</p>
        <p><strong>11.2 Jurisdiction</strong> — Legal proceedings shall be instituted exclusively before the competent courts of Canton of Zug, Switzerland.</p>
        <p><strong>11.3 Mandatory Arbitration</strong> — Disputes shall be resolved by binding arbitration under the Swiss Rules of International Arbitration. One (1) arbitrator, seated in Zug, conducted in English.</p>
        <p><strong>11.4 Waiver of Class Action</strong> — TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, YOU WAIVE YOUR RIGHT TO PARTICIPATE IN ANY CLASS ACTION, COLLECTIVE ACTION, OR REPRESENTATIVE PROCEEDING AGAINST ELVOAQ AG.</p>
      </div>

      <div className="hp-info-card">
        <h3>12. Privacy</h3>
        <p>Your use of the Platform is subject to Elvoaq AG's Privacy Policy, incorporated herein by reference. By using the Platform, you consent to the collection, processing, and use of your information as described in the Privacy Policy.</p>
      </div>

      <div className="hp-info-card">
        <h3>13. Amendments</h3>
        <p>Elvoaq AG reserves the right to modify these Terms at any time. We will provide notice of material changes by updating the Effective Date and, where practicable, by email or Platform notification. Continued use constitutes acceptance of revised Terms.</p>
      </div>

      <div className="hp-info-card">
        <h3>14. Termination</h3>
        <p>These Terms remain in effect until terminated. You may terminate by closing your Account and ceasing use. Elvoaq AG may terminate immediately without notice for breach. Upon termination, all licences cease. Sections 4, 5, 6, 7, 8, 11, and 14 survive termination.</p>
      </div>

      <div className="hp-info-card">
        <h3>15. General Provisions</h3>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Severability:</strong> Invalid provisions shall be modified to the minimum extent necessary</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Waiver:</strong> No failure or delay in exercising any right shall operate as a waiver</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Assignment:</strong> You may not assign rights without prior written consent. Elvoaq AG may assign freely</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Force Majeure:</strong> No liability for failure caused by acts of God, war, terrorism, pandemic, or other events beyond reasonable control</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Language:</strong> In the event of conflict between English and any translation, the English version prevails</li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>16. Contact Information</h3>
        <p>
          <strong>Elvoaq AG</strong> — Legal Department<br />
          Email: <strong>legal@elvoaq.com</strong><br />
          Abuse/Violations: <strong>abuse@elvoaq.com</strong>
        </p>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '16px' }}>© 2026 Elvoaq AG. All rights reserved. Elvoaq and associated marks are trademarks of Elvoaq AG.</p>
      </div>

    </div>
  </div>
);

/* ════════════════════════════════════════════════
   COOKIE POLICY
   ════════════════════════════════════════════════ */
export const CookiePolicyPage = () => (
  <div className="hp hp-info-page">
    <PageNav title="Cookie Policy" />
    <div className="hp-info-page__hero">
      <h1>Cookie <span className="gradient-text">Policy</span></h1>
      <p>Effective Date: 19 February 2026 · Governing Law: Swiss Federal Act on Data Protection (nFADP) · Canton of Zug, Switzerland</p>
    </div>
    <div className="hp-info-page__content hp-info-page__content--text">

      <div className="hp-info-card">
        <h3>1. What Are Cookies?</h3>
        <p>
          Cookies are small text files placed on your device when you visit a website. They help us provide you with a
          personalised, secure, and functional experience. Cookies may contain identifiers, settings, or other data
          that is read back when you return to our platform. Similar technologies — such as local storage, session
          storage, and pixel tags — may also be used and are covered by this policy.
        </p>
        <p>
          ELVOAQ AG uses cookies strictly in accordance with the Swiss Federal Act on Data Protection (nFADP / revDSG),
          the EU General Data Protection Regulation (GDPR) where applicable, and the EU ePrivacy Directive. We only
          place non-essential cookies after you have provided prior, informed, and freely given consent.
        </p>
      </div>

      <div className="hp-info-card">
        <h3>2. Categories of Cookies We Use</h3>
        <p><strong>2.1 Strictly Necessary Cookies (Always Active)</strong></p>
        <p>These cookies are essential for the platform to function. They cannot be disabled because without them
          core services like login, security, and navigation would not work. No consent is required to set these cookies.</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>__eq_cc</strong> — Stores your cookie consent preferences. Duration: 365 days.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_sess</strong> — Secure session identifier for authenticated users. Duration: Session (cleared when browser closes).</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_csrf</strong> — Cross-Site Request Forgery (CSRF) protection token. Duration: Session.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_auth</strong> — Authentication token (HttpOnly, Secure, SameSite=None for cross-origin). Duration: 7 days.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_rt</strong> — Refresh token for silent re-authentication. Duration: 30 days.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_2fa</strong> — Two-factor authentication session flag. Duration: 30 days.</li>
        </ul>
        <p style={{ marginTop: '16px' }}><strong>2.2 Functional / Preference Cookies (Optional)</strong></p>
        <p>These cookies remember your preferences to improve your experience. They are only set with your consent.</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_theme</strong> — Your chosen UI theme (light, dark, or system). Duration: 365 days.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_lang</strong> — Your preferred language (en, de, cs, sk). Duration: 365 days.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_tz</strong> — Detected timezone for calendar and reminder features. Duration: 30 days.</li>
        </ul>
        <p style={{ marginTop: '16px' }}><strong>2.3 Analytics Cookies (Optional)</strong></p>
        <p>These cookies help us understand how users interact with the platform so we can improve it. All data is first-party, anonymised, and never shared with advertising networks.</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_vid</strong> — Anonymous visitor identifier (random UUID). Duration: 1 year.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>eq_sc</strong> — Session counter (number of times you have visited). Duration: 1 year.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Page view events and feature usage counters (stored server-side, linked only to anonymous visitor ID).</li>
        </ul>
        <p style={{ marginTop: '16px' }}><strong>2.4 Third-Party / Social Cookies (Optional)</strong></p>
        <p>These are set by third-party services integrated into the platform. They are only active if you consent to this category.</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>g_state</strong> — Google Identity Services cookie used for Google Sign-In. Set by Google LLC, USA. Duration: Session. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> Apple Sign-In — No persistent cookie is set directly by ELVOAQ; Apple's own session cookie may be set by appleid.apple.com. <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple Privacy Policy</a></li>
        </ul>
      </div>

      <div className="hp-info-card">
        <h3>3. How to Manage Your Cookie Preferences</h3>
        <p>You have full control over which optional cookies are active at any time. You can change your preferences in two ways:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Cookie Settings button</strong> — available at the bottom of every page under "Cookie Settings". This reopens the cookie consent manager where you can update your choices for functional, analytics, and third-party cookies.</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>Browser settings</strong> — all modern browsers allow you to view, block, and delete cookies. Note: blocking strictly necessary cookies will prevent you from signing in or using the platform.</li>
        </ul>
        <p style={{ marginTop: '12px' }}>To open cookie settings: scroll to the footer and click "Cookie Settings", or visit the platform and look for the cookie banner on your first visit.</p>
      </div>

      <div className="hp-info-card">
        <h3>4. Cookies and Local Storage</h3>
        <p>In addition to cookies, ELVOAQ uses browser <strong>localStorage</strong> to store the following data locally on your device:</p>
        <ul className="hp-info-list">
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>app_language</strong> — your chosen language preference</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>app_theme</strong> — your chosen theme preference</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>cookieConsent</strong> — backup of your consent state</li>
          <li><FontAwesomeIcon icon={faCheckCircle} /> <strong>tutorial_complete</strong> — whether you have completed the onboarding tutorial</li>
        </ul>
        <p style={{ marginTop: '12px' }}>LocalStorage data is stored only on your device and is not transmitted to our servers as part of network requests, except when explicitly used in application logic (e.g., language-aware API calls).</p>
      </div>

      <div className="hp-info-card">
        <h3>5. Data Transfers</h3>
        <p>
          Cookies and analytics data collected by ELVOAQ are stored on servers located within the European Union (Frankfurt, Germany region).
          Third-party cookies (Google, Apple) may result in data transfers to countries outside the EU/EEA. Where applicable,
          such transfers are governed by Standard Contractual Clauses (SCCs) as approved by the European Commission and
          recognised under Swiss data protection law.
        </p>
      </div>

      <div className="hp-info-card">
        <h3>6. Retention Periods</h3>
        <p>Cookie retention periods are listed in Section 2. After expiry, cookies are automatically deleted by your browser.
          You can also delete cookies at any time through your browser settings.
          Analytics data linked to anonymous visitor IDs is retained for a maximum of 13 months before automatic deletion.</p>
      </div>

      <div className="hp-info-card">
        <h3>7. Changes to This Cookie Policy</h3>
        <p>ELVOAQ AG may update this Cookie Policy to reflect changes in the cookies we use, legal requirements, or platform changes.
          We will update the "Effective Date" at the top and, for material changes, notify you via an updated cookie consent banner
          or platform notification. Continued use of the platform after the effective date constitutes acceptance.</p>
      </div>

      <div className="hp-info-card">
        <h3>8. Contact</h3>
        <p>
          If you have questions about our use of cookies or wish to exercise your data subject rights, please contact:<br /><br />
          <strong>Elvoaq AG</strong> — Data Protection Officer<br />
          Email: <strong>privacy@elvoaq.com</strong><br />
          Canton of Zug, Switzerland
        </p>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '16px' }}>© 2026 Elvoaq AG. All rights reserved. This Cookie Policy was last updated: 19 February 2026.</p>
      </div>

    </div>
  </div>
);
